import React, { useEffect, useMemo } from 'react';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { connect } from 'react-redux';
import { Row, Col, Container, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import renderField from '../../../shared/components/custom/Field';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import renderSwitchField from '../../../shared/components/custom/Switch';
import { partial } from '../../../util/translation/translation';
import { getImageHash } from '../../../util/functions';
import TextEditor2 from '../../../shared/components/text-editor/TextEditor2';
import renderSelectField from '../../../shared/components/form/Select';
import useConfig from '../../../util/useConfig';
import moment from 'moment';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import validateLotteryForm from './validateLotteryForm';
import renderLotteryDatePickerField from './renderLotteryDatePickerField';
import { LOTTERY_WEBSITE_URL } from '../../../util/api';

const LotteryForm = ({
  lottery,
  handleSubmit,
  initialize,
  type,
  lotteryPrizes,
  formValues,
  clone,
}) => {
  const p = useMemo(() => partial('lotteries'), []);

  const config = useConfig();
  const prizesOptions = useMemo(() => {
    return lotteryPrizes.map((prize) => ({
      label: prize.title,
      value: prize.id,
    }));
  }, [lotteryPrizes]);
  const drawTypeOptions = [
    { label: p('drawType-automatic'), value: 'automatic' },
    { label: p('drawType-manual'), value: 'manual' },
  ];
  const lotteryTypeOptions = [
    { label: p('lotteryType-match'), value: 'MATCH_LOTTERY' },
  ];
  const countdownLink = useMemo(() => {
    return `${LOTTERY_WEBSITE_URL}/lotteries/countdown/${lottery?.id}`;
  }, [lottery]);

  const s = partial('shared');
  useEffect(() => {
    if (lottery) {
      initialize({
        name: lottery.name,
        description: lottery.description,
        isActive: !!lottery.isActive,
        headingImage: {
          name: 'lottery image',
          preview: lottery.imageUrl,
          croppedImage: lottery.imageUrl,
          hash: getImageHash(lottery.imageUrl),
        },
        homeImage: {
          name: 'lottery home image',
          preview: lottery.homeImageUrl,
          croppedImage: lottery.homeImageUrl,
          hash: getImageHash(lottery.homeImageUrl),
        },
        sponsorImage: {
          name: 'lottery sponsor image',
          preview: lottery.sponsorImageUrl,
          croppedImage: lottery.sponsorImageUrl,
          hash: getImageHash(lottery.sponsorImageUrl),
        },
        awayImage: {
          name: 'lottery away image',
          preview: lottery.awayImageUrl,
          croppedImage: lottery.awayImageUrl,
          hash: getImageHash(lottery.awayImageUrl),
        },
        startDate: moment(lottery.startDate).format('YYYY-MM-DDTHH:mm'),
        endDate: lottery.endDate
          ? moment(lottery.endDate).format('YYYY-MM-DDTHH:mm')
          : null,
        ticketPrice: lottery.ticketPrice,
        sponsorLink: lottery.sponsorLink,
        sponsorName: lottery.sponsorName,
        winningSplitPercent: lottery.winningSplitPercent,
        maxPrizePerWinner: lottery.maxPrizePerWinner,

        drawType: drawTypeOptions.find(
          (option) => option.value === lottery.drawType
        ),
        type: lotteryTypeOptions.find(
          (option) => option.value === lottery.type
        ),
        prizes: lottery.lotteryPrizes.map((lotteryPrize) => ({
          label: lotteryPrize.prize.title,
          value: lotteryPrize.prize.id,
        })),
        dynamicLink: lottery.dynamicLink,
        countdownLink,
      });
    }
  }, [lottery]);
  return (
    <form className="form" onSubmit={handleSubmit}>
      <div
        style={{
          backgroundColor: '#F4F4F6',
          borderRadius: 4,
          borderColor: '#D7DAE0',
          borderStyle: 'solid',
          borderWidth: 1,
          marginBottom: 10,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderColor: '#D7DAE0',
            padding: '10px 10px 10px 10px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                fontSize: 16,
                lineHeight: '24px',
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              {s('image')}
            </div>
          </div>
        </div>
        <div style={{ padding: '10px' }}>
          <Container>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">{p('optionalImagesNote')}</span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">{p('imageBanner')}</span>
                  <Field
                    saveBase64Cropped
                    name="headingImage"
                    maxSize={3}
                    component={renderDropZoneField}
                    displayCrop
                    crop="BANNER_CROP"
                  />
                  <span className="form-header">
                    <strong>{s('note')}:</strong> {s('recommendedImageSize')}{' '}
                    16:9 (1035 x 582 px)
                  </span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{p('imageHome')}</span>
                  <Field
                    saveBase64Cropped
                    name="homeImage"
                    maxSize={3}
                    component={renderDropZoneField}
                    displayCrop
                    crop="SPOTLIGHT_SQUARE_CROP"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{p('imageAway')}</span>
                  <Field
                    saveBase64Cropped
                    name="awayImage"
                    maxSize={3}
                    component={renderDropZoneField}
                    displayCrop
                    crop="SPOTLIGHT_SQUARE_CROP"
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                {/* The home and away images above are square */}
                <span className="form-header">
                  <strong>{s('note')}:</strong> {s('recommendedImageSize')} 1:1
                  (750 x 750 px)
                </span>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <div
        style={{
          backgroundColor: '#F4F4F6',
          borderRadius: 4,
          borderColor: '#D7DAE0',
          borderStyle: 'solid',
          borderWidth: 1,
          marginTop: 10,
          marginBottom: 10,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderColor: '#D7DAE0',
            padding: '10px 10px 10px 10px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                fontSize: 16,
                lineHeight: '24px',
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              {s('details')}
            </div>
          </div>
        </div>
        <div style={{ padding: '10px' }}>
          <Container>
            <Row>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{s('name')}*</span>
                  <Field
                    name="name"
                    component={renderField}
                    placeholder={s('name')}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form__form-group flex-Vcenter">
                  <span className="form-header" />
                  <Field
                    name="isActive"
                    component={renderSwitchField}
                    label={[`${s('active')}`, `${s('inActive')}`]}
                    defaultChecked
                    className="colored-click"
                  />
                </div>
              </Col>
            </Row>
            <Row className="lottery-form-dates-row">
              <Col md={6}>
                <div
                  className="form__form-group"
                  style={{ position: 'relative', zIndex: 2 }}
                >
                  <span className="form-header">{s('startDate')}*</span>
                  <Field
                    name="startDate"
                    component={renderLotteryDatePickerField}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div
                  className="form__form-group"
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  <span className="form-header">
                    {s('endDate')}
                    {formValues?.drawType?.value === 'automatic' ? '*' : ''}
                  </span>
                  <Field
                    name="endDate"
                    component={renderLotteryDatePickerField}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{p('drawType')}*</span>
                  <Field
                    name="drawType"
                    placeholder={p('drawType')}
                    options={drawTypeOptions}
                    component={renderSelectField}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{s('type')}*</span>
                  <Field
                    name="type"
                    placeholder={s('type')}
                    options={lotteryTypeOptions}
                    component={renderSelectField}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="form__form-group">
                  <span className="form-header">{s('description')}</span>
                  <div className="marginTop-5" />
                  <div className="form__form-group form__form-group-id">
                    <Field name="description" component={TextEditor2} />
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <div
        style={{
          backgroundColor: '#F4F4F6',
          borderRadius: 4,
          borderColor: '#D7DAE0',
          borderStyle: 'solid',
          borderWidth: 1,
          marginTop: 10,
          marginBottom: 10,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderColor: '#D7DAE0',
            padding: '10px 10px 10px 10px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                fontSize: 16,
                lineHeight: '24px',
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              {s('sponsor')}
            </div>
          </div>
        </div>
        <div style={{ padding: '10px' }}>
          <Container>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">{p('sponsorImageLabel')}</span>
                  <Field
                    saveBase64Cropped
                    name="sponsorImage"
                    maxSize={3}
                    component={renderDropZoneField}
                    displayCrop
                    crop="BANNER_CROP"
                    // The app shows the logo 32px high and as wide as its
                    // own proportions (up to 140px), so any shape works
                    shapes={['FREE', 'SQUARE', 'WIDE']}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{p('sponsorName')}</span>
                  <Field
                    name="sponsorName"
                    component={renderField}
                    placeholder={p('sponsorName')}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{p('sponsorLink')}</span>
                  <Field
                    name="sponsorLink"
                    component={renderField}
                    placeholder={p('sponsorLink')}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <div
        style={{
          backgroundColor: '#F4F4F6',
          borderRadius: 4,
          borderColor: '#D7DAE0',
          borderStyle: 'solid',
          borderWidth: 1,
          marginTop: 10,
          marginBottom: 10,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderColor: '#D7DAE0',
            padding: '10px 10px 10px 10px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                fontSize: 16,
                lineHeight: '24px',
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              {p('prizes')}
            </div>
          </div>
        </div>
        <div style={{ padding: '10px' }}>
          <Container>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <span className="form-header">{p('selectPrizes')}</span>
                  <Field
                    name="prizes"
                    placeholder={p('selectPrizes')}
                    options={prizesOptions}
                    component={renderSelectField}
                    multiple
                  />
                </div>
              </Col>
            </Row>
            {lottery ? (
              <Col>
                <Row>
                  <div className="form__form-group">
                    <span className="form-header marginRight-8">
                      {s('universalLink')}
                    </span>
                    <div className="form__form-group-field">
                      <Field name="dynamicLink" component="input" disabled />
                      <button
                        type="button"
                        className="form__form-group-icon"
                        onClick={() =>
                          navigator.clipboard.writeText(lottery.dynamicLink)
                        }
                      >
                        <LinkVariantIcon />
                      </button>
                    </div>
                  </div>
                </Row>
                <Row>
                  <div className="form__form-group">
                    <span className="form-header marginRight-8">
                      {p('countdownLink')}
                    </span>
                    <div className="form__form-group-field">
                      <Field name="countdownLink" component="input" disabled />
                      <button
                        type="button"
                        className="form__form-group-icon"
                        onClick={() =>
                          navigator.clipboard.writeText(countdownLink)
                        }
                      >
                        <LinkVariantIcon />
                      </button>
                    </div>
                  </div>
                </Row>
              </Col>
            ) : null}
          </Container>
        </div>
      </div>
      <div
        style={{
          backgroundColor: '#F4F4F6',
          borderRadius: 4,
          borderColor: '#D7DAE0',
          borderStyle: 'solid',
          borderWidth: 1,
          marginTop: 10,
          marginBottom: 10,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderColor: '#D7DAE0',
            padding: '10px 10px 10px 10px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                fontSize: 16,
                lineHeight: '24px',
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              {p('payment')}
            </div>
          </div>
        </div>
        <div style={{ padding: '10px' }}>
          <Container>
            <Row>
              <Col md={4}>
                <div className="form__form-group">
                  <span className="form-header">{p('pricePerTicket')}*</span>
                  <div className="form__form-group-field">
                    <Field
                      name="ticketPrice"
                      component={renderField}
                      type="number"
                      placeholder={p('pricePerTicket')}
                    />
                    <div className="form__form-group-icon">
                      &nbsp;{config.currencyShortSmall}&nbsp;
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="form__form-group">
                  <span className="form-header">{s('pricePercent')}*</span>
                  <Field
                    name="winningSplitPercent"
                    component={renderField}
                    placeholder={s('pricePercent')}
                    type="number"
                    min={0}
                    max={100}
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="form__form-group">
                  <span className="form-header">{p('maxPrizePerWinner')}*</span>
                  <Field
                    name="maxPrizePerWinner"
                    component={renderField}
                    placeholder={p('maxPrizePerWinner')}
                    type="number"
                    min={0}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <Col>
        <Button color="primary" type="submit">
          {type === 'create'
            ? `${s('create')}`
            : clone
              ? `${s('clone')}`
              : `${s('save')}`}
        </Button>
        <Link to={`/lottery`}>
          <Button type="button">{s('close')}</Button>
        </Link>
      </Col>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'lottery_form',
  validate: validateLotteryForm,
});
const mapStateToProps = (state) => ({
  formValues: getFormValues('lottery_form')(state),
});
export default connect(mapStateToProps)(reduxF(LotteryForm));
